import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PageHead, Card } from "@/pages/app/AppShell";
import { api, errMsg, fmtDate } from "@/lib/api";

export const KINDS = {
  meeting: ["Meeting", "bg-sky-400"],
  campaign: ["Campaign", "bg-fuchsia-400"],
  event: ["Event", "bg-[#E0218A]"],
  deadline: ["Contract deadline", "bg-red-400"],
  activation: ["Activation", "bg-orange-400"],
  deliverable: ["Deliverable", "bg-teal-300"],
  renewal: ["Renewal", "bg-emerald-400"],
  other: ["Other", "bg-slate-300"],
};
const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Calendar() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", kind: "campaign", date: "", end_date: "", notes: "" });

  const load = () => api.get("/calendar").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const shown = items.filter((i) => !filter || i.kind === filter);
  const byDay = useMemo(() => {
    const m = {};
    shown.forEach((i) => { const k = i.date.slice(0, 10); (m[k] = m[k] || []).push(i); });
    return m;
  }, [shown]);

  const days = useMemo(() => {
    const first = new Date(cursor);
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(first);
    start.setDate(1 - offset);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const shift = (n) => { const d = new Date(cursor); d.setMonth(d.getMonth() + n); setCursor(d); };
  const today = key(new Date());
  const monthItems = shown.filter((i) => i.date.slice(0, 7) === key(cursor).slice(0, 7));

  const save = async () => {
    try {
      await api.post("/calendar", f);
      setOpen(false);
      setF({ title: "", kind: "campaign", date: "", end_date: "", notes: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (id) => { await api.delete(`/calendar/${id}`).catch(() => {}); load(); };

  return (
    <div data-testid="calendar-page">
      <PageHead kicker="Shared with your team" title="Partnership calendar">
        <button className="btn-gold !py-2.5" onClick={() => setOpen(true)} data-testid="calendar-add-button"><Plus className="h-4 w-4" />Add date</button>
      </PageHead>
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-5" data-testid="calendar-filters">
        <button onClick={() => setFilter("")} className={`shrink-0 rounded-full px-3 py-1.5 text-xs ${!filter ? "bg-[#2A1240] text-white font-bold" : "border border-[#2A1240]/15 text-slate-200"}`}>All</button>
        {Object.entries(KINDS).map(([k, [l, c]]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`calendar-filter-${k}`} className={`shrink-0 flex items-center gap-2 rounded-full px-3 py-1.5 text-xs ${filter === k ? "bg-[#2A1240] text-white font-bold" : "border border-[#2A1240]/15 text-slate-200"}`}>
            <span className={`h-2 w-2 rounded-full ${c}`} />{l}
          </button>
        ))}
      </div>
      <Card className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display text-2xl" data-testid="calendar-month-label">{cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</p>
          <div className="flex gap-2">
            <button onClick={() => shift(-1)} className="grid h-9 w-9 place-items-center rounded-full border border-[#2A1240]/15 hover:border-[#E0218A]" aria-label="Previous month" data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => shift(1)} className="grid h-9 w-9 place-items-center rounded-full border border-[#2A1240]/15 hover:border-[#E0218A]" aria-label="Next month" data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="hidden md:grid grid-cols-7 gap-px bg-[#2A1240]/10 rounded-xl overflow-hidden" data-testid="calendar-grid">
          {WEEK.map((w) => <div key={w} className="bg-[#FFF0F7] px-2 py-2 text-[11px] uppercase tracking-wider text-slate-400">{w}</div>)}
          {days.map((d) => {
            const k = key(d);
            const list = byDay[k] || [];
            const inMonth = d.getMonth() === cursor.getMonth();
            return (
              <div key={k} className={`min-h-[104px] bg-[#FFFFFF] p-2 ${inMonth ? "" : "opacity-40"}`}>
                <p className={`text-xs ${k === today ? "inline-grid h-6 w-6 place-items-center rounded-full bg-[#E0218A] text-white font-bold" : "text-slate-300"}`}>{d.getDate()}</p>
                <div className="mt-1 space-y-1">
                  {list.slice(0, 3).map((i) => (
                    <p key={i.id} title={i.title} className="flex items-center gap-1.5 truncate text-[11px] text-slate-200"><span className={`h-1.5 w-1.5 shrink-0 rounded-full ${KINDS[i.kind]?.[1]}`} />{i.title}</p>
                  ))}
                  {list.length > 3 && <p className="text-[11px] text-slate-400">+{list.length - 3} more</p>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="md:mt-6 divide-y divide-[#2A1240]/10" data-testid="calendar-agenda">
          <p className="kicker pb-3">This month</p>
          {!monthItems.length && <p className="py-6 text-sm text-slate-400">Nothing scheduled this month.</p>}
          {monthItems.map((i) => (
            <div key={i.id} className="py-3 flex items-center gap-3" data-testid={`calendar-item-${i.id}`}>
              <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${KINDS[i.kind]?.[1]}`} />
              <span className="w-24 shrink-0 text-xs text-slate-400">{fmtDate(i.date, { month: "short", day: "numeric" })}</span>
              <span className="flex-1 text-sm text-[#2A1240] truncate">{i.link ? <Link to={i.link} className="hover:underline">{i.title}</Link> : i.title}</span>
              <span className="hidden sm:block text-xs text-slate-400">{KINDS[i.kind]?.[0]}</span>
              {i.editable && <button onClick={() => del(i.id)} className="text-slate-400 hover:text-red-300" aria-label="Delete" data-testid={`calendar-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10" data-testid="calendar-dialog">
          <DialogTitle className="font-display text-2xl font-normal">Add to calendar</DialogTitle>
          <input className="field" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="calendar-title-input" />
          <select className="field" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="calendar-kind-select">
            {["meeting", "campaign", "activation", "deliverable", "deadline", "other"].map((k) => <option key={k} value={k}>{KINDS[k][0]}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input className="field" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="calendar-date-input" />
            <input className="field" type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="calendar-enddate-input" />
          </div>
          <textarea className="field" rows={2} placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="calendar-notes-input" />
          <button className="btn-gold" disabled={!f.title || !f.date} onClick={save} data-testid="calendar-save-button">Save</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
